import {DateTime} from "luxon";
import {singleton} from "tsyringe";
import {appState} from "../app.state";
import {buildUserInfo} from "../authentication/build-user-info";

/**
 * Backend response shape for `GET /api/v1/auth/whoami`.
 * Identical to the success-path of `POST /api/v1/auth/login`.
 */
type WhoamiResponse = {
    reason: string;
    reasonCode: number;
    success: boolean;
    employeeUid: string | null;
    username?: string;
    /**
     * The requesting user's OWN uid, transport spelling — NOT `employeeUid`,
     * which is the linked EMPLOYEE's uid and a different identifier entirely.
     * It is what lets the client answer "do I hold this lock?".
     */
    userUid?: string | null;
    privileges: string[];
    token: {
        hash: string | null;
        validUntil: string;
    };
};

/**
 * Hydrates `appState.user` and `appState.privileges` from the backend
 * after a page reload when an auto-login cookie is still valid.
 *
 * Without this, a freshly reloaded page (with valid cookie but cleared
 * localStorage) would redirect to `/auth/login` because `appState.user`
 * is null — even though the backend would happily authenticate any
 * subsequent request via the cookie.
 */
@singleton()
export class AuthHydrationService {

    /**
     * Calls `GET /api/v1/auth/whoami` and populates `appState.user` if a
     * valid session is detected. No-op if `appState.user` is already set.
     *
     * @returns true when hydration set a user, false otherwise (already
     *          set, 401, network error, or invalid response).
     */
    public async hydrateFromCookie(): Promise<boolean> {
        // Skip when we already have a logged-in user — avoid pointless
        // round-trip during normal navigation.
        if (appState.isUserLoggedIn) {
            return false;
        }

        try {
            // Direct fetch instead of BackendClient to avoid the global
            // 401 → 'o2d.session-expired' side effect, which would show
            // a "session expired" toast to users who never logged in.
            const response = await fetch(`/api/v1/auth/whoami`, {
                method: 'GET',
                credentials: 'include',
                cache: 'no-cache',
                headers: {'Content-Type': 'application/json'}
            });

            if (response.status !== 200) {
                return false;
            }

            const payload = (await response.json()) as { data: WhoamiResponse };
            const result = payload?.data;

            if (!result || !result.success || !result.token?.validUntil) {
                return false;
            }

            const validUntil = DateTime.fromISO(result.token.validUntil);
            if (!validUntil.isValid || validUntil < DateTime.utc()) {
                return false;
            }

            // Through the shared factory, never by hand. The login path builds
            // the same object independently, and this path returns early when
            // somebody is already logged in — so it can never repair an omission
            // there. Two hand-written assignments drift; one factory cannot.
            appState.user = buildUserInfo(result);
            appState.privileges = result.privileges ?? [];

            return true;
        } catch (error) {
            // 401 (no session), network errors, or malformed responses:
            // fail-soft — leave appState untouched, normal login flow takes over.
            console.warn('[AuthHydrationService] Whoami hydration failed:', error);
            return false;
        }
    }
}
